import {
  LocationAcuityCode,
  LocationAcuityDescription,
  LocationCodeDescription,
  LocationTypeCode,
} from '@quromedical/fhir-common';
import {
  type CreateLocation,
  type LocationSearchParams,
  SupportedLocationStatus,
} from '@quromedical/models';
import { compact, startcase } from '@quromedical/utils';
import type { Field, SelectOption } from 'components/types';
import type { OrganizationFetcher } from 'core/group';
import { locationParentFetcher } from 'integration';
import { strings } from 'strings';

export const getDescription = (code?: LocationTypeCode): string | undefined => {
  if (!code) {
    return undefined;
  }

  return LocationCodeDescription[code];
};

export const typeOptions = Object.values(LocationTypeCode).map<SelectOption>(
  (code) => ({
    value: code,
    display: LocationCodeDescription[code] || code,
  }),
);

export const acuityOptions = Object.values(
  LocationAcuityCode,
).map<SelectOption>((code) => ({
  value: code,
  display: LocationAcuityDescription[code] || code,
}));

export const initialSearch: Partial<LocationSearchParams> = {};

const statusFetcher = SupportedLocationStatus.map((value) => ({
  value,
  display: startcase(value),
}));

const parentFetcher = locationParentFetcher();

export const searchFields: Field<LocationSearchParams>[] = [
  {
    subfields: [
      {
        type: 'combobox-single',
        key: 'parent',
        label: strings.LocationParent,
        placeholder: strings.ParentPlaceholder,
        fetcher: parentFetcher,
        searchable: true,
      },
      {
        type: 'combobox-single',
        key: 'status',
        label: strings.LabelStatus,
        placeholder: strings.StatusPlaceholder,
        fetcher: [
          {
            display: 'Show All',
            value: undefined,
          },
          ...statusFetcher,
        ],
      },
      {
        type: 'combobox-single',
        key: 'type',
        label: strings.LabelLocationType,
        placeholder: strings.LocationSelectType,
        fetcher: [
          {
            display: 'Show All',
            value: undefined,
          },
          ...typeOptions,
        ],
      },
    ],
  },
];

export const createFields = (
  organizationFetcher: OrganizationFetcher,
  selectedType: LocationTypeCode,
  initialOrganization?: SelectOption,
  initialParent?: SelectOption,
): Field<CreateLocation>[] => {
  const isVirtualHospital = selectedType === LocationTypeCode.VirtualHospital;
  const baseField: Field<CreateLocation>[] = [
    {
      subfields: [
        {
          type: 'text-box',
          key: 'name',
          label: strings.LabelName,
          placeholder: strings.NamePlaceholder,
        },
        {
          type: 'combobox-single',
          key: 'status',
          label: strings.LabelStatus,
          placeholder: strings.StatusPlaceholder,
          fetcher: statusFetcher,
        },
      ],
    },
    {
      subfields: [
        {
          type: 'text-box',
          key: 'description',
          label: strings.TaskLabelDescription,
          placeholder: strings.DescriptionPlaceholder,
        },
        {
          type: 'combobox-single',
          key: 'type',
          label: strings.LabelLocationType,
          fetcher: typeOptions,
        },
      ],
    },
    {
      subfields: compact([
        {
          type: 'combobox-single',
          key: 'managingOrganization',
          label: strings.LabelManagingOrganization,
          searchable: true,
          initialSelection: initialOrganization ? [initialOrganization] : [],
          placeholder: strings.PlaceHolderSelectOrganization,
          fetcher: organizationFetcher,
        },
        !isVirtualHospital && {
          type: 'combobox-single',
          key: 'parent',
          label: strings.LocationParent,
          placeholder: strings.ParentPlaceholder,
          fetcher: parentFetcher,
          searchable: true,
          isDisabled: isVirtualHospital,
          initialSelection: initialParent ? [initialParent] : undefined,
        },
      ]),
    },
    {
      subfields: compact([
        !isVirtualHospital && {
          type: 'number-box',
          key: 'capacity',
          label: strings.LabelLocationCapacity,
          keyboardType: 'decimal-pad',
          placeholder: strings.PlaceHolderLocationCapacity,
        },
        !isVirtualHospital && {
          type: 'combobox-single',
          key: 'acuity',
          label: strings.LabelLocationAcuity,
          fetcher: acuityOptions,
          searchable: false,
        },
      ]),
    },
  ];

  return baseField;
};
